import { useState, useEffect } from 'react';

interface LogEntry {
  type: 'log' | 'error' | 'warn';
  message: string;
  timestamp: Date;
}

export function DebugLog() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);

  useEffect(() => {
    // Only enable in development or when there's a debug flag
    const isDebug = localStorage.getItem('debug') === 'true' || location.search.includes('debug=1');
    if (!isDebug) return;

    const originalConsole = {
      log: console.log,
      error: console.error,
      warn: console.warn,
    };

    const addLog = (type: 'log' | 'error' | 'warn', ...args: any[]) => {
      const message = args.map(arg => {
        if (arg instanceof Error) return arg.message + '\n' + arg.stack;
        if (typeof arg === 'object') return JSON.stringify(arg, null, 2);
        return String(arg);
      }).join(' ');

      const entry: LogEntry = { type, message, timestamp: new Date() };
      setLogs(prev => {
        const newLogs = [...prev, entry].slice(-50); // Keep last 50 logs
        return newLogs;
      });

      if (type === 'error') {
        setHasErrors(true);
        setIsVisible(true);
      }
    };

    console.log = (...args) => {
      originalConsole.log(...args);
      addLog('log', ...args);
    };

    console.error = (...args) => {
      originalConsole.error(...args);
      addLog('error', ...args);
    };

    console.warn = (...args) => {
      originalConsole.warn(...args);
      addLog('warn', ...args);
    };

    // Capture unhandled errors
    const handleError = (e: ErrorEvent) => {
      addLog('error', 'Unhandled Error:', e.message, e.error?.stack);
      setHasErrors(true);
      setIsVisible(true);
    };

    const handleRejection = (e: PromiseRejectionEvent) => {
      addLog('error', 'Unhandled Promise Rejection:', e.reason);
      setHasErrors(true);
      setIsVisible(true);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      console.log = originalConsole.log;
      console.error = originalConsole.error;
      console.warn = originalConsole.warn;
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  // Enable debug mode by triple-clicking the title area
  useEffect(() => {
    let clickCount = 0;
    let lastClickTime = 0;

    const handleClick = () => {
      const now = Date.now();
      if (now - lastClickTime < 500) {
        clickCount++;
        if (clickCount >= 3) {
          localStorage.setItem('debug', 'true');
          setIsVisible(true);
          alert('Debug mode enabled! Reload to see logs.');
          clickCount = 0;
        }
      } else {
        clickCount = 1;
      }
      lastClickTime = now;
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 max-h-[50vh] bg-gray-900 text-white font-mono text-xs">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-800 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <span className="font-bold">Debug Log</span>
          {hasErrors && <span className="px-2 py-0.5 bg-red-600 rounded text-xs">ERROR</span>}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setLogs([])}
            className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded"
          >
            Clear
          </button>
          <button
            onClick={() => setIsVisible(false)}
            className="px-2 py-1 text-xs bg-gray-700 hover:bg-gray-600 rounded"
          >
            Close
          </button>
        </div>
      </div>
      <div className="overflow-auto p-2 space-y-1 max-h-[40vh]">
        {logs.length === 0 ? (
          <div className="text-gray-500 italic">No logs yet...</div>
        ) : (
          logs.map((log, i) => (
            <div
              key={i}
              className={`break-all whitespace-pre-wrap ${
                log.type === 'error' ? 'text-red-400' :
                log.type === 'warn' ? 'text-yellow-400' : 'text-green-400'
              }`}
            >
              <span className="text-gray-500">[{log.timestamp.toLocaleTimeString()}]</span>{' '}
              {log.message}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
